import { createOperatorClient, type OperatorClient } from '@social-studio/operator-client';
import { CSRF_HEADER, readCsrfToken } from './session';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * The single place the console talks to the API (ADR-033): the generated
 * operator client, same origin through Caddy (and the Vite proxy in development),
 * so the operator cookies are sent. Direct `fetch` anywhere else fails lint.
 */
export const api: OperatorClient = createOperatorClient({
  // Absolute, because `Request` rejects relative URLs outside a browser document.
  baseUrl: globalThis.location?.origin ?? '',
  credentials: 'same-origin',
  // Resolved per call, not captured once: keeps the client testable.
  fetch: (request) => globalThis.fetch(request),
});

// CSRF double submit: state-changing requests echo the operator CSRF cookie.
api.use({
  onRequest({ request }) {
    const token = readCsrfToken();
    if (token && !SAFE_METHODS.has(request.method)) request.headers.set(CSRF_HEADER, token);
    return request;
  },
});

const PROBLEM_SLUG = /\/problems\/([a-z-]+)$/;

/** The problem slug of an error body (`…/problems/invalid-credentials` → `invalid-credentials`). */
export function problemSlug(error: unknown): string | undefined {
  return PROBLEM_SLUG.exec(String((error as { type?: unknown } | undefined)?.type ?? ''))?.[1];
}

/** The `errors[].code` values for one field of a `422 validation-failed`. */
export function fieldErrorCodes(error: unknown, field: string): string[] {
  const errors = (error as { errors?: { field?: string; code?: string }[] } | undefined)?.errors ?? [];
  return errors.filter((entry) => entry.field === field && entry.code).map((entry) => entry.code!);
}

/** A failed call: the slug for a problem answer, `network` when no answer came. */
export class ApiFailure extends Error {
  constructor(
    readonly slug: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(slug);
  }
}

/** Runs one generated-client call; resolves its data or rejects with an `ApiFailure`. */
export async function call<T>(request: () => Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let result;
  try {
    result = await request();
  } catch {
    throw new ApiFailure('network', 0);
  }
  if (!result.response.ok) throw new ApiFailure(problemSlug(result.error) ?? 'unexpected', result.response.status, result.error);
  return result.data as T;
}
